import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import i18next from 'i18next';
import { createStack } from '@famly/mf_layout_stack';
import { Box, createBox } from '@famly/mf_layout_box';
import { Text } from '@famly/mf_data-display_text';
import { useTranslation } from '@famly/mf_util_translation';
import { hasValue } from '@famly/stat_ts-utils_has-value';
import { exhaustiveCheck } from '@famly/stat_ts-utils_exhaustive-check';
import { useDataProps } from '@famly/mf_ts-utils_data-aria-props';
import { Icon } from '@famly/mf_data-display_icon';
import { NumericPill } from '@famly/mf_data-display_numeric-pill';
import { Button } from '@famly/mf_input_button';
import { useAutoDismiss } from './use-auto-dismiss';
import { truncateToastMessage, isToastMessageTruncatable } from './truncate-toast-message';
const getColors = (severity) => {
    switch (severity) {
        case 'success':
            return {
                textColor: 'n0',
                backgroundColor: 'g400',
                outlineColor: 'g500',
                occurencesColor: 'g400',
                occurencesBgColor: 'g100',
                dismissColor: 'n0',
            };
        case 'error':
            return {
                textColor: 'n0',
                backgroundColor: 'r300',
                outlineColor: 'g500',
                occurencesColor: 'r400',
                occurencesBgColor: 'r100',
                dismissColor: 'n0',
            };
        case 'neutral':
            return {
                textColor: 'n400',
                backgroundColor: 'b100',
                outlineColor: 'b400',
                occurencesColor: 'b100',
                occurencesBgColor: 'b300',
                dismissColor: 'b300',
            };
        default:
            exhaustiveCheck(severity);
            return {
                textColor: 'n400',
                backgroundColor: 'b100',
                outlineColor: 'b400',
                occurencesColor: 'b100',
                occurencesBgColor: 'b300',
                dismissColor: 'b300',
            };
    }
};
const getIcon = (severity, size) => {
    const iconSize = size === 'large' ? 32 : 24;
    switch (severity) {
        case 'success':
            return _jsx(Icon, { name: "done_all", color: "g100", size: iconSize });
        case 'error':
            return _jsx(Icon, { name: "emergency_home", color: "r100", size: iconSize });
        case 'neutral':
            return _jsx(Icon, { name: "info", color: "b400", size: iconSize });
        default:
            exhaustiveCheck(severity);
            return _jsx(Icon, { name: "info", color: "b400", size: iconSize });
    }
};
export const Toast = (props) => {
    const { message, occurences, severity, errorCode, onDismiss, duration, size = 'regular', announce = true } = props;
    const isLarge = size === 'large';
    const announcementRole = severity === 'error' ? 'alert' : 'status';
    const dataProps = useDataProps(props);
    const errorHeaderText = useTranslation('Toast.errorCode');
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [isCopied, setIsCopied] = React.useState(false);
    const isTruncatable = isToastMessageTruncatable(severity, message);
    const isTruncated = isTruncatable && !isExpanded;
    // Reading past the fold takes longer than the countdown allows, so expanding hands the toast
    // to the user: the timer stops and the close button appears in its place.
    const remainingDuration = isExpanded ? undefined : duration;
    const isSelfDismissing = hasValue(remainingDuration);
    const isDismissible = hasValue(onDismiss) && !isSelfDismissing;
    useAutoDismiss({ duration: remainingDuration, onDismiss, occurences });
    const icon = getIcon(severity, size);
    const messageLines = (isTruncated ? truncateToastMessage(message) : message).split('\n');
    /**
     * On a toast tall enough to stack content, the icon and the close button belong beside the
     * first line — centred against six lines of text they read as detached from the message they
     * mark. A single-line toast is centred, because its content is shorter than the toast's
     * minimum height.
     */
    const isTall = messageLines.length > 1 || hasValue(errorCode) || isTruncatable;
    return (_jsxs(FocusableStack, { direction: "row", alignItems: isTall ? 'flex-start' : 'center', minHeight: getMinHeight(size, isDismissible), boxSizing: "border-box", backgroundColor: getColors(severity).backgroundColor, gap: 2, paddingY: isLarge ? 5 : 4, paddingLeft: isLarge ? 5 : 4, paddingRight: isLarge ? 4 : 3, borderRadius: 2, 
        // The toast sits over whatever the user was working on, and needs to read as floating
        // above it rather than as a band painted across it.
        elevation: 3, outlineColor: getColors(severity).outlineColor, tabIndex: 0, role: announce ? announcementRole : undefined, "aria-atomic": announce || undefined, ...dataProps, children: [icon, _jsxs(Stack, { marginRight: "auto", gap: 2, alignItems: "flex-start", children: [_jsx(Stack, { direction: "column", gap: 2, "data-e2e-class": "toast-message", children: messageLines.map((line, index) => (_jsx(Text, { variant: isLarge ? 'h5' : 'body', color: getColors(severity).textColor, children: line }, index))) }), hasValue(errorCode) ? (_jsx(Box, { display: "inline-block", backgroundColor: "r100", color: "r400", borderRadius: 6, paddingX: 2, children: _jsx(Text, { variant: "micro", "data-e2e-class": "toast-error-code", children: `${errorHeaderText}: ${errorCode}` }) })) : null, isTruncated ? (_jsx(Button, { text: i18next.t($ => $.mf.dataDisplay_toast.src.translations.json.seeAll), icon: "visibility", variant: "critical", size: "compact", onClick: () => setIsExpanded(true), "data-e2e-class": "toast-see-all" })) : null, isTruncatable && isExpanded ? (_jsxs(Stack, { direction: "row", alignItems: "center", gap: 2, children: [_jsx(Button, { text: i18next.t($ => $.mf.dataDisplay_toast.src.translations.json.copyDetails), icon: "content_copy", variant: "critical", size: "compact", onClick: async () => {
                                    // Only confirm a copy that happened. The clipboard is unavailable
                                    // outside a secure context and refusable by the browser, and
                                    // telling somebody their error details are on the clipboard when
                                    // they are not costs a support round trip.
                                    try {
                                        await navigator.clipboard.writeText(message);
                                        setIsCopied(true);
                                    }
                                    catch (_a) {
                                        setIsCopied(false);
                                    }
                                }, "data-e2e-class": "toast-copy-details" }), _jsx(Box, { role: announce ? undefined : 'status', children: isCopied ? (_jsx(FadeInBox, { "data-e2e-class": "toast-copied", children: _jsx(Text, { variant: "body", color: getColors(severity).textColor, children: i18next.t($ => $.mf.dataDisplay_toast.src.translations.json.copied) }) })) : null })] })) : null] }), hasValue(occurences) && occurences > 1 ? (_jsx(Box, { marginLeft: 6, children: _jsx(NumericPill, { count: occurences, color: getColors(severity).occurencesColor, backgroundColor: getColors(severity).occurencesBgColor, "data-e2e-class": "toast-count" }) })) : null, isDismissible ? (_jsx(IconButton, { "aria-label": i18next.t($ => $.mf.dataDisplay_toast.src.translations.json.closeButtonAriaLabel), size: isLarge ? 'medium' : 'small', "data-e2e-class": "toast-close", onClick: onDismiss, children: _jsx(Icon, { name: "close", color: getColors(severity).dismissColor, size: isLarge ? 32 : 24 }) })) : null] }));
};
/**
 * Keeps a one-line toast from collapsing to the height of its text. The dismissible heights leave
 * room for the close button at the icon size for that toast size, which is taller than the line.
 */
const getMinHeight = (size, isDismissible) => {
    if (size === 'large') {
        return isDismissible ? '84px' : '72px';
    }
    return isDismissible ? '68px' : '56px';
};
/**
 * The confirmation appears next to a button the user has just pressed, where an instant swap reads
 * as a flicker. Fading it in ties it to the press.
 */
const FadeInBox = styled(createBox({}))({
    '@keyframes toastCopiedFadeIn': {
        from: { opacity: 0 },
        to: { opacity: 1 },
    },
    animation: 'toastCopiedFadeIn 0.5s ease',
});
const FocusableStack = styled(createStack({}))(({ outlineColor }) => ({
    '&:focus-visible': {
        outline: `2px solid ${outlineColor}`,
    },
}));
